import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useSetAtom } from "jotai";
import { useFragment } from "react-relay";

import { useJoinBookingAndNavigate } from "@/features/JoinBookingModal";
import { teeSheetRequestBookingInfoAtom } from "@/features/TeeSheet/atoms";
import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import { Button } from "@whoosh/design-system/src/components/controls";
import { JoinWaitlistIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import { VStack } from "@whoosh/design-system/src/components/primitive";
import { useBreakpointValue } from "@whoosh/design-system/src/hooks/useBreakpointValue";

import { useFlags } from "../../utils/launchDarkly";
import { AgendaWaitlistBodyFragment$key } from "./__generated__/AgendaWaitlistBodyFragment.graphql";
import { AgendaWaitlistBookings } from "./components/AgendaWaitlistBookings";

type AgendaWaitlistBodyProps = {
  fragmentRef: AgendaWaitlistBodyFragment$key | null;
  onJoinWaitlist?: (queueSlotId: string, requestedTime: string | null) => void;
};

export const AgendaWaitlistBody = ({
  fragmentRef,
  onJoinWaitlist,
}: AgendaWaitlistBodyProps) => {
  const data = useFragment(AgendaWaitlistBodyFragmentString, fragmentRef);
  const agenda = data?.agendas?.edges ? data?.agendas?.edges[0]?.node : null;

  const trackEvent = useTrackEvent();

  const supportsPublic = agenda?.facility.club?.supportsPublic ?? false;

  const { joinBooking: joinWaitlistBooking } = useJoinBookingAndNavigate();

  const { waitlistRestrictions } = useFlags();
  const setRequestBookingInfo = useSetAtom(teeSheetRequestBookingInfoAtom);
  const handleCreateNewBookingRequest = (
    queueSlotId?: string,
    requestedTime?: string
  ) => {
    if (!queueSlotId) {
      return;
    }
    const normalizedRequestedTime = requestedTime ?? null;
    if (onJoinWaitlist) {
      onJoinWaitlist(queueSlotId, normalizedRequestedTime);
    } else {
      setRequestBookingInfo({
        queueSlotId,
        requestedTime: normalizedRequestedTime,
      });
    }
    trackEvent("Agenda.WaitlistJoinButtonClicked", {
      queueSlotId,
    });
  };

  const hasWaitlistRestrictions =
    !!data?.agendas?.edges?.[0]?.node?.waitlistRestrictions?.edges?.length;

  const { showToast } = useToast();

  const handleJoinWaitlist = () => {
    return agenda?.queueSlot.id
      ? handleCreateNewBookingRequest(agenda?.queueSlot?.id ?? "")
      : showToast({
          title: t`Waitlist not available`,
          description: t`Please contact the club for more information`,
          semanticStyle: "error",
        });
  };

  const isMobileScreen = useBreakpointValue({
    base: true,
    xs: true,
    sm: true,
    md: true,
    agendaSplit: false,
    eventCardColumn: false,
    lg: false,
    xl: false,
  });

  return (
    <VStack gap={6}>
      {hasWaitlistRestrictions && waitlistRestrictions && !isMobileScreen ? (
        <Button
          variant="secondary"
          accessibilityLabel={t`Join Waitlist button`}
          leftIcon={<JoinWaitlistIcon />}
          onPress={handleJoinWaitlist}
        >
          {t`Join Waitlist`}
        </Button>
      ) : null}

      {!supportsPublic && agenda ? (
        <AgendaWaitlistBookings
          agendaRef={agenda}
          onWaitlistBookingPress={joinWaitlistBooking}
        />
      ) : null}
    </VStack>
  );
};

export const AgendaWaitlistBodyFragmentString = graphql`
  fragment AgendaWaitlistBodyFragment on Facility {
    agendas(dateGte: $date, dateLte: $date) {
      edges {
        node {
          id
          ...AgendaWaitlistBookings_Agenda
          facility {
            club {
              supportsPublic
            }
          }
          waitlistRestrictions: bookingRules(
            type: [WAITLIST]
            courseIds: $courseIds
          ) {
            edges {
              liftAt
              node {
                id
              }
            }
          }
          queueSlot {
            id
          }
        }
      }
    }
  }
`;
